import apiClient from './api';
import { accountsService } from './accountsService';

// Public customer endpoints sit outside the staff JWT, so they use raw fetch rather than the interceptor.
const PUBLIC_BASE = process.env.REACT_APP_API_URL || '/api';

async function publicPost(path, body) {
  const res = await fetch(`${PUBLIC_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || 'Request failed. Please try again.');
  }
  return data;
}

// Admin CRUD (staff JWT via apiClient) under /api/customers/admin/customers/.
export const customerAdminService = {
  // Backed by the unified accounts list endpoint (type=customer).
  list: async ({ search = '', page = 1, pageSize = 20, order = '' } = {}) =>
    accountsService.list({ type: 'customer', search, page, pageSize, order }), // { results, total, page, page_size }
  get: async (id) => {
    const { data } = await apiClient.get(`/customers/admin/customers/${id}/`);
    return data.data;
  },
  create: async (payload) => {
    const { data } = await apiClient.post('/customers/admin/customers/', payload);
    return data.data;
  },
  update: async (id, payload) => {
    const { data } = await apiClient.patch(`/customers/admin/customers/${id}/`, payload);
    return data.data;
  },
  remove: async (id) => {
    const { data } = await apiClient.delete(`/customers/admin/customers/${id}/`);
    return data;
  },
  setPassword: async (id, newPassword) => {
    const { data } = await apiClient.post(`/customers/admin/customers/${id}/password/`, {
      new_password: newPassword,
    });
    return data;
  },
};

// Public customer auth plus session helpers; the customer token is stored under its own distinct key.
const TOKEN_KEY = 'customerAuthToken';
const PROFILE_KEY = 'customerProfile';

export const customerAuth = {
  login: (email, password) => publicPost('/customers/auth/login/', { email, password }),
  register: (payload) => publicPost('/customers/auth/register/', payload),
  requestOtp: (phone) => publicPost('/customers/auth/otp/request/', { phone }),
  verifyOtp: (phone, code, name, salutation = '') =>
    publicPost('/customers/auth/otp/verify/', { phone, code, name, salutation }),

  save: (token, customer) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(PROFILE_KEY, JSON.stringify(customer || {}));
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PROFILE_KEY);
  },
  token: () => localStorage.getItem(TOKEN_KEY) || '',
  profile: () => {
    try {
      return JSON.parse(localStorage.getItem(PROFILE_KEY) || 'null');
    } catch {
      return null;
    }
  },
  isLoggedIn: () => !!localStorage.getItem(TOKEN_KEY),
};

export default customerAdminService;
